import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, History, Wrench, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, SectionTitle, LoadingState, ErrorState, StatusBadge } from "@/components/common";
import { JobTracker, PhotoGrid } from "@/modules/interventions/shared";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { fmtCents, fmtDate, fmtDateTime, fmtNumber } from "@/lib/format";
import { t } from "@/i18n";
import { downloadFile } from "@/lib/api";

const brand = { background: "var(--brand)", color: "var(--brand-contrast)" };

function Observations({ items }) {
  if (!items.length) return <p className="text-sm text-slate-500">{t("workshop.noNotes")}</p>;
  return <ul className="space-y-2">{items.map((o) => <li key={o.id} className="rounded-lg bg-slate-50 p-3 text-sm" data-testid={`portal-obs-${o.id}`}>{o.text}</li>)}</ul>;
}

export default function PortalJob() {
  const { id } = useParams();
  const { garage, slug } = usePortal();
  const { me } = useAuth();
  const { data, error, loading, reload } = useFetch(`/portal/${slug}/jobs/${id}`);
  if (me === null) return <Navigate to={`/g/${slug}/login`} replace />;
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="portal-job">
      <Link to={`/g/${slug}/account?tab=workshop`} className="inline-flex items-center gap-1 text-sm text-slate-600"><ArrowLeft className="h-4 w-4" /> {t("workshop.back")}</Link>
      <div>
        <p className="font-mono text-sm font-semibold" style={{ color: "var(--brand)" }}>{data.number}</p>
        <h1 className="text-3xl font-bold text-slate-900">{data.vehicle.plate} · {data.vehicle.make} {data.vehicle.model}</h1>
        <p className="mt-1 text-sm text-slate-600">{data.reason}</p>
        {data.report_shared && <button type="button" className="mt-3 text-sm font-semibold underline" style={{ color: "var(--brand)" }} onClick={() => downloadFile(`/portal/${slug}/jobs/${data.id}/report.pdf`)} data-testid="portal-report-download">{t("b8.report.download")}</button>}
        {data.diagnostics?.map((d) => <button key={d.id} type="button" className="mt-3 block text-sm font-semibold underline" style={{ color: "var(--brand)" }} onClick={() => downloadFile(`/portal/${slug}/diagnostics/${d.id}/report.pdf`)} data-testid={`portal-diag-download-${d.id}`}>{t("b9.diag.portal")}: {d.name} (PDF)</button>)}
      </div>
      <Card><p className="mb-4 font-semibold" data-testid="portal-job-status-text">{t(`workshop.statusText.${data.status}`)}</p><JobTracker status={data.status} testId="portal-job-tracker" /></Card>
      {data.quotes.length > 0 && (
        <Card testId="portal-job-quotes">
          <SectionTitle>{t("quotes.title")}</SectionTitle>
          {data.quotes.map((q) => (
            <Link key={q.id} to={`/g/${slug}/quotes/${q.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm hover:bg-slate-50" data-testid={`portal-job-quote-${q.number}`}>
              <span className="font-mono font-semibold">{q.number} v{q.version}</span>
              <span className="flex items-center gap-2"><strong>{fmtCents(q.total_cents, garage.currency)}</strong><StatusBadge status={q.status} /></span>
            </Link>
          ))}
        </Card>
      )}
      <Card testId="portal-job-observations"><SectionTitle>{t("workshop.notesFromWorkshop")}</SectionTitle><Observations items={data.observations} /></Card>
      <Card testId="portal-job-photos"><SectionTitle>{t("jobs.photos")}</SectionTitle><PhotoGrid photos={data.photos} testId="portal-photo-grid" /></Card>
    </div>
  );
}

export function WorkshopCards({ slug, tz, currency, showQuotes = true }) {
  const { data } = useFetch(`/portal/${slug}/workshop`);
  if (!data) return null;
  const job = data.jobs[0];
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="portal-workshop-cards">
      <Card testId="portal-current-job">
        <Wrench className="h-5 w-5" style={{ color: "var(--brand)" }} />
        <p className="mt-3 font-display text-lg font-bold">{t("workshop.currentJob")}</p>
        {job ? <>
          <p className="mt-1 text-sm text-slate-700" data-testid="portal-current-job-text">{job.vehicle.plate} · {t(`status.${job.status}`)}</p>
          <div className="mt-3"><JobTracker status={job.status} testId="portal-current-job-tracker" /></div>
          <Button asChild size="sm" className="mt-4" style={brand} data-testid="portal-open-job"><Link to={`/g/${slug}/jobs/${job.id}`}>{t("workshop.follow")}</Link></Button>
        </> : <p className="mt-1 text-sm text-slate-600">{t("workshop.noJob")}</p>}
      </Card>
      {showQuotes && <Card testId="portal-quotes-awaiting">
        <FileText className="h-5 w-5" style={{ color: "var(--brand)" }} />
        <p className="mt-3 font-display text-lg font-bold">{t("workshop.quotesAwaiting")} <span className="ml-1 rounded-full bg-amber-100 px-2 text-xs text-amber-900" data-testid="portal-quotes-awaiting-count">{data.quotes.length}</span></p>
        {data.quotes.length === 0 ? <p className="mt-1 text-sm text-slate-600">{t("workshop.noQuotes")}</p> : (
          <ul className="mt-2 space-y-2">{data.quotes.map((q) => (
            <li key={q.id}><Link to={`/g/${slug}/quotes/${q.id}`} className="flex justify-between rounded-lg border border-slate-200 p-2 text-sm hover:bg-slate-50" data-testid={`portal-awaiting-quote-${q.number}`}>
              <span className="font-mono">{q.number}</span><strong>{fmtCents(q.total_cents, currency)}</strong></Link></li>
          ))}</ul>
        )}
      </Card>}
    </div>
  );
}

export function WorkshopTab({ slug, tz, currency }) {
  const jobs = useFetch(`/portal/${slug}/jobs`);
  const quotes = useFetch(`/portal/${slug}/quotes`);
  if (!jobs.data || !quotes.data) return <LoadingState />;
  return (
    <div className="space-y-6" data-testid="portal-workshop-tab">
      <div>
        <SectionTitle>{t("workshop.jobs")}</SectionTitle>
        {jobs.data.length === 0 ? <p className="text-sm text-slate-500">{t("workshop.noJobs")}</p> : jobs.data.map((j) => (
          <Link key={j.id} to={`/g/${slug}/jobs/${j.id}`} className="mb-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm hover:bg-slate-50" data-testid={`portal-job-row-${j.number}`}>
            <span><span className="font-mono font-semibold">{j.number}</span> · {j.vehicle.plate} <span className="text-slate-500">{fmtDate(j.created_at, tz)}</span></span><StatusBadge status={j.status} />
          </Link>
        ))}
      </div>
      <div>
        <SectionTitle>{t("quotes.title")}</SectionTitle>
        {quotes.data.length === 0 ? <p className="text-sm text-slate-500">{t("workshop.noQuotes")}</p> : quotes.data.map((q) => (
          <Link key={q.id} to={`/g/${slug}/quotes/${q.id}`} className="mb-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm hover:bg-slate-50" data-testid={`portal-quote-row-${q.number}`}>
            <span className="font-mono font-semibold">{q.number} v{q.version}</span><span className="flex items-center gap-2"><strong>{fmtCents(q.total_cents, currency)}</strong><StatusBadge status={q.status} /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function VehicleHistoryButton({ slug, vehicle, tz }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`vehicle-history-${vehicle.plate}`}><History className="mr-1 h-4 w-4" /> {t("workshop.history")}</Button>
      {open && <VehicleHistoryDialog slug={slug} vehicle={vehicle} tz={tz} onClose={() => setOpen(false)} />}
    </>
  );
}

function VehicleHistoryDialog({ slug, vehicle, tz, onClose }) {
  const { data } = useFetch(`/portal/${slug}/vehicles/${vehicle.id}/history`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="vehicle-history-dialog">
        <DialogHeader><DialogTitle>{t("workshop.historyTitle", { plate: vehicle.plate })}</DialogTitle><DialogDescription>{t("workshop.historyHint")}</DialogDescription></DialogHeader>
        {!data ? <LoadingState /> : data.length === 0 ? <p className="text-sm text-slate-500">{t("workshop.noHistory")}</p> : data.map((j) => (
          <div key={j.id} className="space-y-2 border-b border-slate-100 pb-4" data-testid={`history-job-${j.number}`}>
            <p className="font-semibold">{fmtDateTime(j.completed_at, tz)} · <span className="font-mono">{j.number}</span>{j.odometer_in ? ` · ${fmtNumber(j.odometer_in)} km` : ""}</p>
            <p className="text-sm text-slate-600">{j.reason}</p>
            <Observations items={j.observations} />
            {j.photos.length > 0 && <PhotoGrid photos={j.photos} />}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
